import { fitsInlinePreview, type PreviewSize } from '@sd/client-core';
import type { FileInfo } from '@sd/contracts';
import { inlinePlayback, isInlinePreviewable } from '@sd/contracts';
import { useEffect, useRef, useState, type RefObject } from 'react';
import { fileBadge, formatBytes } from '../format';
import { Icon } from '../icons/Icon';
import { Button, IconButton } from '../ui/controls';
import { useConfirm, useToast } from '../ui/overlays';
import { useAppActions, useClient, useClientState } from './context';
import { errorMessage } from '../errors';
import { scanLabel, unscannableReason } from './copy';

export function FileIcon({ ext, small, style }: { ext: string; small?: boolean; style?: React.CSSProperties }) {
  const badge = fileBadge(ext);
  return (
    <span className={`fc-ic ${badge.cls}`} style={small ? { width: 34, height: 40, borderRadius: 10, fontSize: 9, ...style } : style}>
      {badge.label}
    </span>
  );
}

export function ScanTag({ file }: { file: FileInfo }) {
  const s = scanLabel(file.scan);
  return (
    <span className={`scan ${s.cls}`}>
      <Icon name={s.icon} className={s.spin ? 'spin' : undefined} />
      {s.text}
    </span>
  );
}

/** Starts a download, confirming first for files the scanner could not inspect. */
export function useDownload() {
  const client = useClient();
  const confirm = useConfirm();
  const toast = useToast();
  return async (file: FileInfo) => {
    if (file.scan === 'infected') return toast(errorMessage('FILE_INFECTED'));
    if (file.scan === 'pending' || file.scan === 'scanning') return toast(errorMessage('FILE_NOT_READY'));
    // Programs come only where the company allows them, and then never without this.
    if (
      file.executable &&
      !(await confirm({
        title: '실행 파일이에요',
        body: `${file.name}은(는) 열면 실행되는 프로그램이에요. 보낸 사람에게 직접 확인한 파일만 내려받고 실행하세요.`,
        ok: '내려받기',
        danger: true,
      }))
    )
      return;
    let acknowledgeUnscanned = false;
    if (file.scan === 'unscannable') {
      const ok = await confirm({
        title: '검사할 수 없는 파일이에요',
        body: `${unscannableReason(file.scanDetail)} 악성코드 검사를 하지 못했어요. 보낸 사람을 믿을 수 있을 때만 내려받으세요.`,
        ok: '그래도 다운로드',
      });
      if (!ok) return;
      acknowledgeUnscanned = true;
    }
    await client.download(file, { acknowledgeUnscanned });
    if (client.state.downloads[file.id]?.state === 'browser') toast('다운로드를 시작했어요. 저장됐는지는 브라우저에서 확인해 주세요');
  };
}

/**
 * `.fcard` attachment card with preview and download state (desktop shows real completion).
 * `tabbable`: the message is the timeline's one Tab stop (its controls are reached from there).
 */
export function FileCard({ file, tabbable }: { file: FileInfo; tabbable: boolean }) {
  const env = useAppActions();
  const download = useDownload();
  const client = useClient();
  const state = useClientState((s) => s.downloads[file.id]);
  const blocked = file.scan === 'infected';
  const label =
    state?.state === 'done' ? (
      <>
        <Icon name="folder" size="sm" />
        저장 완료 · 폴더 열기
      </>
    ) : state?.state === 'browser' ? (
      <>
        <Icon name="check" size="sm" />
        다운로드 시작됨
      </>
    ) : state?.state === 'busy' ? (
      <>
        <Icon name="sync" size="sm" className="spin" />
        저장 중…{state.totalBytes ? ` ${Math.min(99, Math.floor((state.receivedBytes / state.totalBytes) * 100))}%` : ''}
      </>
    ) : (
      <>
        <Icon name="download" size="sm" />
        다운로드
      </>
    );
  const tabIndex = tabbable ? undefined : -1;
  return (
    <div className="fcard">
      <div className="fc-top">
        <FileIcon ext={file.ext} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="fc-name ell" title={file.name}>
            {file.name}
          </div>
          <div className="fc-meta">
            {formatBytes(file.size)} · <ScanTag file={file} />
            {file.executable ? (
              <>
                {' · '}
                <span className="scan warn">
                  <Icon name="alert" />
                  실행 파일
                </span>
              </>
            ) : null}
          </div>
        </div>
      </div>
      <MediaPlayer file={file} tabIndex={tabIndex} />
      <div className="fc-acts">
        <Button size="sm" variant="ghost" icon="eye" tabIndex={tabIndex} onClick={() => env.openModal({ kind: 'preview', file })} disabled={blocked}>
          미리보기
        </Button>
        <Button
          size="sm"
          className={state?.state === 'done' ? 'saved' : undefined}
          disabled={blocked}
          tabIndex={tabIndex}
          onClick={() => (state?.state === 'done' ? void client.showInFolder(file.id) : void download(file))}
        >
          {label}
        </Button>
        {state?.state === 'busy' && state.downloadId ? (
          <IconButton icon="x" iconSize="sm" label="다운로드 취소" tip="다운로드 취소" tabIndex={tabIndex} onClick={() => void client.cancelDownload(file.id)} />
        ) : null}
      </div>
    </div>
  );
}

/**
 * Clean video and audio played in place, streamed by the browser in ranges (web). Nothing where the app
 * can only download (desktop) or before the scan is through: the server serves no such file.
 */
export function MediaPlayer({ file, tabIndex, large = false }: { file: FileInfo; tabIndex?: number; large?: boolean }) {
  const client = useClient();
  const kind = file.scan === 'clean' ? inlinePlayback(file.name) : null;
  const url = kind ? client.mediaUrl(file.id) : null;
  if (!kind || !url) return null;
  return kind === 'video' ? (
    <video className={large ? 'fc-video lg' : 'fc-video'} src={url} controls playsInline preload="metadata" tabIndex={tabIndex} aria-label={`${file.name} 재생`} />
  ) : (
    <audio className="fc-audio" src={url} controls preload="metadata" tabIndex={tabIndex} aria-label={`${file.name} 재생`} />
  );
}

/** Images previewed inline; a larger original shows as a file card instead of being fetched for a tile. */
export function isPreviewTile(file: FileInfo): boolean {
  return file.kind === 'image' && fitsInlinePreview(file);
}

/**
 * Object URL for an inline image preview fetched with the session's credentials, released when no longer shown.
 * `first`: wanted now. `size`: the server's small copy for a tile or the viewer's screen (the original where none).
 */
export function usePreviewUrl(file: FileInfo | null, options: { first?: boolean; size?: PreviewSize } = {}): string | null {
  const client = useClient();
  const [url, setUrl] = useState<string | null>(null);
  const fileId = file && file.scan === 'clean' && isInlinePreviewable(file.name) && fitsInlinePreview(file) ? file.id : null;
  const first = options.first === true;
  const size = options.size;
  useEffect(() => {
    if (!fileId) {
      setUrl(null);
      return undefined;
    }
    let alive = true;
    client
      .previewUrl(fileId, { first, size })
      .then((u) => alive && setUrl(u))
      .catch(() => alive && setUrl(null));
    return () => {
      alive = false;
      client.releasePreview(fileId, { size });
    };
  }, [client, fileId, first, size]);
  return url;
}

/**
 * Whether an element has come within a screen of the visible area (and stays so once it has). A conversation mounts
 * every image it has loaded: fetching each tile only as it nears the screen keeps a phone line for the ones looked at.
 */
function useNear(ref: RefObject<Element | null>): boolean {
  const [near, setNear] = useState(() => typeof IntersectionObserver === 'undefined');
  useEffect(() => {
    const el = ref.current;
    if (near || !el) return undefined;
    const observer = new IntersectionObserver((entries) => entries.some((entry) => entry.isIntersecting) && setNear(true), { rootMargin: '100% 0px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, [near, ref]);
  return near;
}

function Shot({ file }: { file: FileInfo }) {
  const ref = useRef<HTMLSpanElement>(null);
  const url = usePreviewUrl(useNear(ref) ? file : null, { size: 'tile' });
  return (
    <span ref={ref} className="shot v1">
      {url ? <img src={url} alt="" decoding="async" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} /> : null}
    </span>
  );
}

/** `.imgs` image grid; opens the lightbox. */
export function ImageGrid({ files, tabbable }: { files: FileInfo[]; tabbable: boolean }) {
  const env = useAppActions();
  return (
    <div className="imgs" style={files.length === 1 ? { gridTemplateColumns: 'minmax(0,1fr)' } : undefined}>
      {files.map((file) => (
        <button key={file.id} type="button" tabIndex={tabbable ? undefined : -1} onClick={() => env.openModal({ kind: 'preview', file })} aria-label={`${file.name} 크게 보기`}>
          <Shot file={file} />
        </button>
      ))}
    </div>
  );
}

/** Upload progress card inside a pending bubble (design m13). */
export function UploadCard({ upload, tabbable, onCancel }: { upload: { name: string; ext: string; size: number; sentBytes: number }; tabbable: boolean; onCancel(): void }) {
  const pct = upload.size ? Math.round((upload.sentBytes / upload.size) * 100) : 0;
  return (
    <div className="fcard up">
      <div className="fc-top">
        <FileIcon ext={upload.ext} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="fc-name ell">{upload.name}</div>
          <div className="fc-meta">
            {formatBytes(upload.sentBytes)} / {formatBytes(upload.size)} · {pct}%
          </div>
        </div>
        <IconButton icon="x" iconSize="sm" label="업로드 취소" tip="업로드 취소" tabIndex={tabbable ? undefined : -1} style={{ color: '#fff', width: 30, height: 30 }} onClick={onCancel} />
      </div>
      <div className="prog" role="progressbar" aria-label={`${upload.name} 업로드`} aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <i style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
